import React, { createContext, useContext, useState, useEffect } from 'react';

// Create Location Context
const LocationContext = createContext();

// Custom hook to use location context
export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
};

// LocationProvider Component
export const LocationProvider = ({ children }) => {
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLocationFetched, setIsLocationFetched] = useState(false);

  // Function to get current location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      const options = {
        enableHighAccuracy: true,
        timeout: 10000, // 10 seconds
        maximumAge: 300000 // 5 minutes cache
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          resolve({ lat, lng });
        },
        (error) => {
          reject(error);
        },
        options
      );
    });
  };

  // Initialize location on app mount (only once)
  useEffect(() => {
    const initializeLocation = async () => {
      // Check if location already exists in localStorage
      const storedLat = localStorage.getItem('user-location-lat');
      const storedLng = localStorage.getItem('user-location-lng');
      const locationFetched = localStorage.getItem('location-fetched');

      // If we already have location stored and fetched flag is set, use it
      if (storedLat && storedLng && locationFetched === 'true') {
        console.log('Using stored location:', { lat: storedLat, lng: storedLng });
        setLocation({ 
          lat: parseFloat(storedLat), 
          lng: parseFloat(storedLng) 
        });
        setIsLocationFetched(true);
        return;
      }

      // Otherwise, get fresh location
      console.log('Getting fresh location...');
      setIsLoading(true);
      setError(null);

      try {
        const position = await getCurrentLocation();
        console.log('Location obtained:', position);
        
        // Update state
        setLocation(position);
        setIsLocationFetched(true);
        
        // Store in localStorage
        localStorage.setItem('user-location-lat', position.lat);
        localStorage.setItem('user-location-lng', position.lng);
        localStorage.setItem('location-fetched', 'true');
        
      } catch (err) {
        console.error('Error getting location:', err);
        let errorMessage = 'Unable to get your location.';
        
        if (err.code === 1) {
          errorMessage = 'Location permission denied. Please allow location access.';
        } else if (err.code === 2) {
          errorMessage = 'Location unavailable. Please check your device settings.';
        } else if (err.code === 3) {
          errorMessage = 'Location request timed out. Please try again.';
        }
        
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    // Only run once when component mounts
    if (!isLocationFetched) {
      initializeLocation();
    }
  }, []); // Empty dependency array - runs only once on mount

  // Function to manually refresh location (if needed)
  const refreshLocation = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const position = await getCurrentLocation();
      console.log('Location refreshed:', position);
      
      setLocation(position);
      setIsLocationFetched(true);
      
      // Update localStorage
      localStorage.setItem('user-location-lat', position.lat);
      localStorage.setItem('user-location-lng', position.lng);
      localStorage.setItem('location-fetched', 'true');
      
    } catch (err) {
      console.error('Error refreshing location:', err);
      let errorMessage = 'Unable to refresh your location.';
      
      if (err.code === 1) {
        errorMessage = 'Location permission denied. Please allow location access.';
      } else if (err.code === 2) {
        errorMessage = 'Location unavailable. Please check your device settings.';
      } else if (err.code === 3) {
        errorMessage = 'Location request timed out. Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to clear location
  const clearLocation = () => {
    setLocation({ lat: null, lng: null });
    setIsLocationFetched(false);
    localStorage.removeItem('user-location-lat');
    localStorage.removeItem('user-location-lng');
    localStorage.removeItem('location-fetched');
  };

  // Context value
  const value = {
    location,
    isLoading,
    error,
    isLocationFetched,
    refreshLocation,
    clearLocation,
    setLocation // Allow manual location updates
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationProvider;

